<template>
  <div class="gs-app gm-custom-fields">
    <!-- Loader -->
    <portal to="loaderTarget">
      <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="isLoading">
        <gd-loader :label="$t('frontend_loading')"/>
      </div>
    </portal>
    
    <div class="gm-custom-fields__workspace flex mb-4 mx-4">
      <div class="gm-custom-fields__surface w-full">
        <navigation-tabs
          class="gm-nav-custom-fields-tabs absolute"
          :nav_tabs="navTabs"
        ></navigation-tabs>

        <client-table v-if="!isLoading"
          class="groovemail-custom-fields-table"
          :tableRef="'customFieldTable'"
          :tableData="customfields"
          :tableColumns="mail_listsTableColumns"
          :tableOptions="mail_listsTableOptions"
          :tableTitle="'frontend_groovemail_custom_fields_title'"
          :tableCondition="customfields.length"
          :slotData="slotData"
          :emptyStateData="emptyStateData"
          :addNewBtnText="'frontend_groovemail_customfields_index_new_custom'"
          @addNewData="openCreateField"
        >
        </client-table>

        <gs-named-modal
          name="add-input-fields"
          :title="$t('frontend_groovemail_campains_show_field')"
          class="fieldmodel"
        >
          <create-update-field
            type="add"
            from="custom-fields"
            :customfieldData="null"
            @showLoader="showLoader"
            @hideLoader="hideLoader"
            @fetchfields="fetchfields"
            @closeCreateFieldModal="closeCreateFieldModal"
          >
          </create-update-field>
        </gs-named-modal>

        <gs-named-modal
          name="edit-input-fields"
          :title="$t('frontend_groovemail_campains_show_field')"
          class="fieldmodel"
        >
          <create-update-field
            type="edit"
            from="custom-fields"
            :customfieldData="customfieldData"
            @showLoader="showLoader"
            @hideLoader="hideLoader"
            @fetchfields="fetchfields"
            @closeEditFieldModal="closeEditFieldModal"
          >
          </create-update-field>
        </gs-named-modal>
      </div>
    </div>

    <!-- Delete custom field confirmation modal -->
    <confirm-modal
      :ref="`confirmation-delete-customField`"
      :name="`confirmation-delete-customField`"
      :buttonText="$t('frontend_delete')"
      :cancelText="$t('frontend_cancel')"
      :confirmMessage="this.$t('frontend_bouses_delete_bonus_title')"
      :messageContent="this.$t('frontend_groovemail_swal_revert_message')"
      @confirm="deleteCustomField"
    />
  </div>
</template>

<script>
import ClientTable from '@groovepages/gd-ui-kit/components/tables/ClientTable.vue';
import Customfield from '@/models/Customfield';
import themeConfig from '@/theme/themeConfig.js';
import NavigationTabs from '@/components/navigation-tabs.vue';
import showAlert from '@/components/sweet-alert-popup.js';
import CreateUpdateField from './create-update-field.vue';

export default {
  name: 'CustomField',
  middleware: 'auth',
  metaInfo() {
    return { title: this.$t('frontend_groovemail_custom_fields_title') };
  },

  data() {
    return {
      customfieldData: {},
      customfields: [],
      fullPageLoader: true,
      isLoading: false,
      customfieldsId: null,
      emptyStateData: {
        emptyStateHeading: 'frontend_groovemail_custom_fields_title',
        emptyStateSubHeading:
          'frontend_groovemail_customfields_index_you_currently',
        emptyStateBtnText: 'frontend_groovemail_customfields_index_new_custom',
      },
      mail_listsTableColumns: ['identifier', 'type', 'default', 'actions'],
      mail_listsTableOptions: {
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        texts: {
          filterPlaceholder: this.$t('frontend_groovemail_tables_search_query'),
        },
        sortable: ['identifier', 'type'],
        filterable: ['identifier', 'type'],
        headings: {
          identifier: () => this.$t('frontend_groovemail_forms_identifier'),
          type: () => this.$t('frontend_groovemail_customfields_index_type'),
          default: () => this.$t('frontend_groovemail_custom_fields_default_field_label'),
          actions: () => this.$t('frontend_groovemail_forms_index_actions'),
        },
        perPage: 10,
        debounce: 1000,
        alwaysShowPerPageSelect: true,
        perPageValues: [10, 25, 50, 100],
        rowClassCallback: () => 'hover:bg-grey-50 funnelRow',
      },
    };
  },

  components: {
    ClientTable,
    NavigationTabs,
    CreateUpdateField,
  },

  computed: {
    navTabs() {
      return [
        {
          tabName: 'frontend_groovemail_components_pricepoint_sidebar_forms',
          tabRoute: 'Forms',
        },
        {
          tabName:
            'frontend_groovemail_components_pricepoint_sidebar_customfields',
          tabRoute: 'CustomField',
        },
      ];
    },
    slotData() {
      return [
        {
          slotName: 'identifier',
          slotType: 'default',
          showDescription: false,
          defaultVal: this.$t('frontend_groovemail_not_available'),
        },
        {
          slotName: 'type',
          slotType: 'default',
          showDescription: false,
          class: 'capitalize',
          defaultVal: this.$t('frontend_groovemail_not_available'),
        },
        {
          slotName: 'default',
          slotType: 'default',
          showDescription: false,
          defaultVal: this.$t('frontend_groovemail_not_available'),
        },
        {
          slotName: 'actions',
          slotType: 'actions',
          actionList: [
            {
              id: 0,
              name: 'frontend_edit',
              conditions: () => {
                return true;
              },
              callback: (props) => this.editCustomfield(props.row.id),
            },
            {
              id: 1,
              name: 'frontend_delete',
              conditions: () => true,
              callback: (props) => this.deleteCustomFieldModal(props.row.id),
            },
          ],
        },
      ];
    },
  },

  methods: {
    async fetchfields() {
      // show loader
      this.showLoader();
      this.customfields = await Customfield.$get();
      // hide loader
      this.hideLoader();
    },

    openCreateField() {
      this.$root.$emit('modal-open', 'add-input-fields');
    },

    closeCreateFieldModal() {
      this.$root.$emit('modal-close', 'add-input-fields');
    },

    closeEditFieldModal() {
      this.$root.$emit('modal-close', 'edit-input-fields');
    },

    // open delete custom filed modal
    async deleteCustomFieldModal(id) {
      // set the custom filed id
      this.customfieldsId = id;

      // open the modal
      this.$refs[`confirmation-delete-customField`].open();
    },

    // close delete custom filed modal
    closeDeleteCustomFieldModal() {
      // reset the custom filed id
      this.customfieldsId = null;

      // close the modal
      this.$refs[`confirmation-delete-customField`].close();
    },

    // delete custom field
    async deleteCustomField() {
      // show loader
      this.showLoader();

      // delete custom field
      const customdelete = new Customfield();
      const response = await customdelete.customfieldDelete(this.customfieldsId);

      // check response if it 204 then delete
      if (response.status === 204) {
        // remove from list
        this.customfields = this.customfields.filter(item => item.id !== this.customfieldsId);
      } else {
        // hide loader
        this.hideLoader();

        // show error alert
        this.backendErrorAlert();
      }

      this.hideLoader();
    },

    async editCustomfield(id) {
      this.showLoader();
      this.customfieldData = await Customfield.$find(id);
      this.$root.$emit('modal-open', 'edit-input-fields');
      this.hideLoader();
    },

    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },

    // backend error alert
    async backendErrorAlert() {
      await showAlert({
        iconType: 'alert-error',
        html : `
          <style>.swal2-cancel { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-sm text-deep-dark"> ${ this.$t('frontend_groovemail_forms_view_unknown_backend_error') }</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t('frontend_close'),
      });
    }
  },

  async created() {
    this.fetchfields();
  },
};
</script>

<style lang="scss">
.groovemail-custom-fields-table .gd-table {
  .VueTables--client table {
    th:nth-child(1),
    th:nth-child(1),
    td:nth-child(3),
    td:nth-child(3) {
      width: 30%;
      word-break: break-all;
    }
    th:nth-child(2),
    th:nth-child(2) {
      width: 20%;
      word-break: break-all;
    }
  }
}

.gm-nav-custom-fields-tabs {
  top: 37px;
  z-index: 10;
}
</style>

<style scoped>
.gm-custom-fields {
  min-height: 100%;
  padding: 28px;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gm-custom-fields__workspace,
.gm-custom-fields__surface {
  min-width: 0;
}

.gm-custom-fields__surface {
  max-width: 1280px;
  margin: 0 auto;
}

.groovemail-custom-fields-table {
  min-width: 0;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

@media (max-width: 640px) {
  .gm-custom-fields {
    padding: 16px;
  }

  .gm-custom-fields__workspace {
    margin-right: 0;
    margin-left: 0;
  }

  .groovemail-custom-fields-table {
    border-radius: var(--gs-radius-md);
  }
}
</style>
